// Worker status types
export type WorkerStatus = 'active' | 'on-leave' | 'inactive';

// Payment frequency types
export type PaymentType = 'daily' | 'weekly' | 'monthly';

// Worker role types
export type WorkerRole = 'owner' | 'manager' | 'worker' | 'viewer';

// Leave request status
export type LeaveRequestStatus = 'pending' | 'approved' | 'rejected';

// Worker permissions based on role
export const defaultPermissions: Record<WorkerRole, string[]> = {
  owner: [
    'manage_workers',
    'manage_tasks',
    'view_analytics',
    'manage_settings',
    'manage_finances',
  ],
  manager: [
    'manage_tasks',
    'view_analytics',
    'view_finances',
  ],
  worker: [
    'view_tasks',
    'update_tasks',
  ],
  viewer: [
    'view_tasks',
  ],
};

// Leave history entry interface
export interface LeaveHistoryEntry {
  id: string;
  startDate: string;
  endDate: string;
  days: number;
  isPaid: boolean;
  reason: string;
  status: LeaveRequestStatus;
  request_date?: string;
  leave_type?: string;
}

// Emergency contact interface
export interface EmergencyContact {
  name: string;
  relationship: string;
  phone: string;
}

// Working hours interface
export interface WorkingHours {
  start: string;
  end: string;
}

// Worker interface
export interface LeaveRequest {
  id: string;
  workerId: string;
  farmId: string;
  startDate: string;
  endDate: string;
  leaveType: 'sick' | 'vacation' | 'personal' | 'maternity' | 'paternity' | 'other';
  reason?: string;
  status: LeaveRequestStatus;
  reviewedBy?: string;
  reviewedAt?: string;
  createdAt: string;
}

export interface ShiftSchedule {
  id: string;
  farmId: string;
  workerId: string;
  startTime: string;
  endTime: string;
  roleTask?: string;
  notes?: string;
  createdAt: string;
}

export interface WorkerTraining {
  id: string;
  farmId: string;
  workerId: string;
  certificationName: string;
  provider?: string;
  completionDate?: string;
  expiryDate?: string;
  status: 'active' | 'expired' | 'pending';
  documentUrl?: string;
  createdAt: string;
}

export interface DisciplinaryRecord {
  id: string;
  farmId: string;
  workerId: string;
  incidentDate: string;
  type: 'warning' | 'suspension' | 'termination' | 'other';
  description?: string;
  actionTaken?: string;
  loggedBy?: string;
  createdAt: string;
}

export interface RecruitmentCandidate {
  id: string;
  farmId: string;
  name: string;
  email?: string;
  phone?: string;
  roleApplied?: string;
  stage: 'applied' | 'screening' | 'interview' | 'offer' | 'hired' | 'rejected';
  resumeUrl?: string;
  interviewDate?: string;
  notes?: string;
  createdAt: string;
}

export interface PayrollRecord {
  id: string;
  workerId: string;
  farmId: string;
  periodStart: string;
  periodEnd: string;
  baseSalary: number;
  deductions: number;
  bonuses: number;
  totalPay: number;
  paymentDate?: string;
  status: 'pending' | 'paid' | 'failed';
  notes?: string;
  createdAt: string;
}

export interface Worker {
  id: string;
  name: string;
  role: WorkerRole;
  phoneNumber: string;
  email: string;
  startDate: string;
  wage: number;
  paymentType: PaymentType;
  status: WorkerStatus;
  specialization: string[];
  department?: string;
  jobTitle?: string;
  employmentType?: 'full_time' | 'part_time' | 'seasonal' | 'casual' | 'contract';
  workingHours: WorkingHours;
  leaveDays: number;
  leaveHistory: LeaveHistoryEntry[];
  emergencyContact: EmergencyContact;
  bankDetails?: {
    bankName: string;
    accountNumber: string;
    accountName: string;
    swiftCode?: string;
  };
  documents?: {
    id: string;
    name: string;
    type: string;
    url: string;
    uploadDate: string;
  }[];
  performanceRating?: number;
  linkedUserId?: string;
  notes: string;
  permissions: string[];
  attendance?: string[];
} 